import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import type {
    AirBnBIntegrationConfig,
    PropertyModules,
} from '../../../../../packages/entities/Property';
import { getEnabledModuleCount } from '../modules/registry';

interface Property {
    id: number;
    name: string;
    minNights: number;
    currency: string;
    country: string;
    weekStartDay: 0 | 1;
    modules: PropertyModules;
    created: string;
    updated: string;
}

interface PropertyCardProps {
    property: Property;
    onClick: () => void;
}

const PropertyCard = ({ property, onClick }: PropertyCardProps) => {
    const moduleCount = getEnabledModuleCount(property.modules);
    const airbnbModule = property.modules[
        'integration/airbnb'
    ] as AirBnBIntegrationConfig | undefined;
    const hasAirBnB = airbnbModule?.enabled;

    return (
        <Box
            onClick={onClick}
            sx={{
                border: '2px solid #374151',
                backgroundColor: '#1a1a2e',
                p: 2,
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                '&:hover': {
                    backgroundColor: 'rgba(99, 102, 241, 0.1)',
                    borderColor: '#6366f1',
                    transform: 'translateX(2px)',
                },
            }}
        >
            <Stack spacing={1.5}>
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                    }}
                >
                    <Typography
                        variant="body1"
                        sx={{
                            fontWeight: 700,
                            fontSize: '1rem',
                        }}
                    >
                        {property.name}
                    </Typography>
                    <Typography
                        variant="body2"
                        sx={{
                            fontWeight: 600,
                            color: '#9ca3af',
                            fontSize: '0.75rem',
                        }}
                    >
                        ID: {property.id}
                    </Typography>
                </Box>

                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                    }}
                >
                    <Box
                        sx={{
                            display: 'inline-block',
                            px: 1.5,
                            py: 0.5,
                            border: '2px solid #6366f1',
                            backgroundColor: 'rgba(99, 102, 241, 0.1)',
                            fontWeight: 700,
                            fontSize: '0.75rem',
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em',
                            color: '#6366f1',
                        }}
                    >
                        {property.currency}
                    </Box>

                    <Stack direction="row" spacing={1} alignItems="center">
                        {moduleCount > 0 && (
                            <Box
                                sx={{
                                    display: 'inline-block',
                                    px: 1.5,
                                    py: 0.5,
                                    border: '2px solid #6366f1',
                                    backgroundColor: 'rgba(99, 102, 241, 0.1)',
                                    fontWeight: 700,
                                    fontSize: '0.75rem',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.05em',
                                    color: '#6366f1',
                                }}
                            >
                                {moduleCount} Active
                            </Box>
                        )}
                        {hasAirBnB && (
                            <Tooltip
                                title={
                                    <Box>
                                        <Typography
                                            sx={{
                                                fontWeight: 700,
                                                fontSize: '0.75rem',
                                            }}
                                        >
                                            AirBnB Listing: {airbnbModule.listingId}
                                        </Typography>
                                        {airbnbModule.lastSyncAt && (
                                            <Typography
                                                sx={{
                                                    fontSize: '0.7rem',
                                                    color: '#9ca3af',
                                                }}
                                            >
                                                Last sync:{' '}
                                                {new Date(
                                                    airbnbModule.lastSyncAt
                                                ).toLocaleString()}
                                            </Typography>
                                        )}
                                    </Box>
                                }
                                arrow
                            >
                                <Box
                                    sx={{
                                        display: 'inline-block',
                                        px: 1,
                                        py: 0.25,
                                        border: '2px solid',
                                        borderColor:
                                            airbnbModule.syncStatus === 'success'
                                                ? '#22c55e'
                                                : airbnbModule.syncStatus === 'error'
                                                  ? '#ef4444'
                                                  : '#f59e0b',
                                        backgroundColor:
                                            airbnbModule.syncStatus === 'success'
                                                ? 'rgba(34, 197, 94, 0.1)'
                                                : airbnbModule.syncStatus === 'error'
                                                  ? 'rgba(239, 68, 68, 0.1)'
                                                  : 'rgba(245, 158, 11, 0.1)',
                                        fontWeight: 700,
                                        fontSize: '0.65rem',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.05em',
                                        color:
                                            airbnbModule.syncStatus === 'success'
                                                ? '#22c55e'
                                                : airbnbModule.syncStatus === 'error'
                                                  ? '#ef4444'
                                                  : '#f59e0b',
                                        cursor: 'help',
                                    }}
                                >
                                    AirBnB
                                </Box>
                            </Tooltip>
                        )}
                    </Stack>
                </Box>
            </Stack>
        </Box>
    );
};

export default PropertyCard;
